import React, { useState, useEffect } from 'react';
import { Table, Card, Button, Space, Tag, Input, Select, DatePicker, message, Modal, Descriptions, Statistic, Row, Col } from 'antd';
import { SearchOutlined, ReloadOutlined, EyeOutlined, WalletOutlined, TransactionOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import api from '../../services/api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

interface WalletRecord {
  id: string;
  memberId: string;
  memberName?: string;
  memberPhone?: string;
  balance: number;
  frozenBalance: number;
  totalRecharge: number;
  totalConsume: number;
  totalGift: number;
  status: number;
  createdAt: string;
  updatedAt: string;
}

interface WalletTransaction {
  id: string;
  walletId: string;
  memberId: string;
  type: string;
  amount: number;
  balanceBefore: number;
  balanceAfter: number;
  orderNo?: string;
  remark?: string;
  createdAt: string;
}

const MemberWallet: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<WalletRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [detailVisible, setDetailVisible] = useState(false);
  const [transactionVisible, setTransactionVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<WalletRecord | null>(null);
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [transactionTotal, setTransactionTotal] = useState(0);
  const [transactionPage, setTransactionPage] = useState(1);
  
  // 搜索条件
  const [keyword, setKeyword] = useState('');
  const [status, setStatus] = useState<number>();

  const fetchData = async () => {
    setLoading(true);
    try {
      const params: any = { page, pageSize };
      if (keyword) params.keyword = keyword;
      if (status !== undefined) params.status = status;
      const res = await api.get('/member-wallets', { params });
      setData(res.data?.list || []);
      setTotal(res.data?.total || 0);
    } catch (error) {
      message.error('获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, pageSize]);

  const handleSearch = () => {
    setPage(1);
    fetchData();
  };

  const handleReset = () => {
    setKeyword('');
    setStatus(undefined);
    setPage(1);
    fetchData();
  };

  const handleViewDetail = (record: WalletRecord) => {
    setCurrentRecord(record);
    setDetailVisible(true);
  };

  const handleViewTransactions = async (record: WalletRecord) => {
    setCurrentRecord(record);
    setTransactionPage(1);
    await fetchTransactions(record.id, 1);
    setTransactionVisible(true);
  };

  const fetchTransactions = async (walletId: string, p: number) => {
    try {
      const res = await api.get(`/member-wallets/${walletId}/transactions`, { params: { page: p, pageSize: 10 } });
      setTransactions(res.data?.list || []);
      setTransactionTotal(res.data?.total || 0);
    } catch (error) {
      message.error('获取交易记录失败');
    }
  };

  const getStatusTag = (status: number) => {
    return status === 1 ? <Tag color="green">正常</Tag> : <Tag color="red">冻结</Tag>;
  };

  const getTransactionTypeTag = (type: string) => {
    const typeMap: Record<string, { color: string; text: string }> = {
      recharge: { color: 'green', text: '充值' },
      consume: { color: 'orange', text: '消费' },
      refund: { color: 'blue', text: '退款' },
      gift: { color: 'purple', text: '赠送' },
      adjust: { color: 'cyan', text: '调整' },
      freeze: { color: 'red', text: '冻结' },
      unfreeze: { color: 'green', text: '解冻' },
    };
    const item = typeMap[type] || { color: 'default', text: type };
    return <Tag color={item.color}>{item.text}</Tag>;
  };

  const columns: ColumnsType<WalletRecord> = [
    { title: '会员姓名', dataIndex: 'memberName', key: 'memberName', width: 120 },
    { title: '手机号', dataIndex: 'memberPhone', key: 'memberPhone', width: 130 },
    { title: '余额', dataIndex: 'balance', key: 'balance', width: 120, render: (v) => <span style={{ color: '#1890ff', fontWeight: 'bold' }}>¥{(v / 100).toFixed(2)}</span> },
    { title: '冻结金额', dataIndex: 'frozenBalance', key: 'frozenBalance', width: 120, render: (v) => `¥${(v / 100).toFixed(2)}` },
    { title: '累计充值', dataIndex: 'totalRecharge', key: 'totalRecharge', width: 120, render: (v) => `¥${(v / 100).toFixed(2)}` },
    { title: '累计消费', dataIndex: 'totalConsume', key: 'totalConsume', width: 120, render: (v) => `¥${(v / 100).toFixed(2)}` },
    { title: '累计赠送', dataIndex: 'totalGift', key: 'totalGift', width: 120, render: (v) => `¥${(v / 100).toFixed(2)}` },
    { title: '状态', dataIndex: 'status', key: 'status', width: 80, render: getStatusTag },
    { title: '更新时间', dataIndex: 'updatedAt', key: 'updatedAt', width: 180 },
    {
      title: '操作',
      key: 'action',
      width: 180,
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleViewDetail(record)}>详情</Button>
          <Button type="link" size="small" icon={<TransactionOutlined />} onClick={() => handleViewTransactions(record)}>流水</Button>
        </Space>
      ),
    },
  ];

  const transactionColumns: ColumnsType<WalletTransaction> = [
    { title: '交易类型', dataIndex: 'type', key: 'type', width: 100, render: getTransactionTypeTag },
    { title: '交易金额', dataIndex: 'amount', key: 'amount', width: 120, render: (v) => {
      const isPositive = v > 0;
      return <span style={{ color: isPositive ? 'green' : 'red' }}>{isPositive ? '+' : ''}{(v / 100).toFixed(2)}</span>;
    }},
    { title: '交易前余额', dataIndex: 'balanceBefore', key: 'balanceBefore', width: 120, render: (v) => `¥${(v / 100).toFixed(2)}` },
    { title: '交易后余额', dataIndex: 'balanceAfter', key: 'balanceAfter', width: 120, render: (v) => `¥${(v / 100).toFixed(2)}` },
    { title: '关联订单', dataIndex: 'orderNo', key: 'orderNo', width: 180 },
    { title: '备注', dataIndex: 'remark', key: 'remark', width: 150 },
    { title: '交易时间', dataIndex: 'createdAt', key: 'createdAt', width: 180 },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card title="钱包余额">
        <Space style={{ marginBottom: 16 }}>
          <Input placeholder="会员姓名/手机号" value={keyword} onChange={e => setKeyword(e.target.value)} style={{ width: 200 }} allowClear />
          <Select placeholder="状态" value={status} onChange={setStatus} style={{ width: 120 }} allowClear>
            <Select.Option value={1}>正常</Select.Option>
            <Select.Option value={0}>冻结</Select.Option>
          </Select>
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>搜索</Button>
          <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
        </Space>
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page, pageSize, total, showSizeChanger: true, showQuickJumper: true,
            showTotal: (t) => `共 ${t} 条`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          }}
          scroll={{ x: 1300 }}
        />
      </Card>

      <Modal title="钱包详情" open={detailVisible} onCancel={() => setDetailVisible(false)} footer={null} width={600}>
        {currentRecord && (
          <>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={8}><Statistic title="当前余额" value={(currentRecord.balance / 100).toFixed(2)} prefix="¥" valueStyle={{ color: '#1890ff' }} /></Col>
              <Col span={8}><Statistic title="冻结金额" value={(currentRecord.frozenBalance / 100).toFixed(2)} prefix="¥" valueStyle={{ color: '#ff4d4f' }} /></Col>
              <Col span={8}><Statistic title="可用余额" value={((currentRecord.balance - currentRecord.frozenBalance) / 100).toFixed(2)} prefix="¥" valueStyle={{ color: '#52c41a' }} /></Col>
            </Row>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="会员姓名">{currentRecord.memberName}</Descriptions.Item>
              <Descriptions.Item label="手机号">{currentRecord.memberPhone}</Descriptions.Item>
              <Descriptions.Item label="累计充值">¥{(currentRecord.totalRecharge / 100).toFixed(2)}</Descriptions.Item>
              <Descriptions.Item label="累计消费">¥{(currentRecord.totalConsume / 100).toFixed(2)}</Descriptions.Item>
              <Descriptions.Item label="累计赠送">¥{(currentRecord.totalGift / 100).toFixed(2)}</Descriptions.Item>
              <Descriptions.Item label="状态">{getStatusTag(currentRecord.status)}</Descriptions.Item>
              <Descriptions.Item label="创建时间">{currentRecord.createdAt}</Descriptions.Item>
              <Descriptions.Item label="更新时间">{currentRecord.updatedAt}</Descriptions.Item>
            </Descriptions>
          </>
        )}
      </Modal>

      <Modal title="交易流水" open={transactionVisible} onCancel={() => setTransactionVisible(false)} footer={null} width={1000}>
        <Table
          columns={transactionColumns}
          dataSource={transactions}
          rowKey="id"
          pagination={{
            current: transactionPage, pageSize: 10, total: transactionTotal,
            onChange: (p) => { setTransactionPage(p); currentRecord && fetchTransactions(currentRecord.id, p); },
          }}
          scroll={{ x: 900 }}
        />
      </Modal>
    </div>
  );
};

export default MemberWallet;
